import React, { useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IntlProvider } from "react-intl";
import StepIndicator from "../components/OnboardingStepIndicator";
import OnboardingPage1 from "./OnboardingPage1";
import OnboardingPage2 from "./OnboardingPage2";
import EmailVerificationContainer from "./EmailVerificationContainer";
import BehalfModeIndicator from "../components/BehalfModeIndicator";
import LocaleContext from "../../state/contexts/LocaleContext";
import { messages } from "../onboarding.i18n";
import { useAuth } from "../../hooks/useAuth";
import { createUser, createClientUser } from "../services/onboardingApiService";
import { FlexColumn } from "../components/OnboardingLayoutComponents";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { useOnboardingMode } from "../hooks/useOnboardingMode";
import { ERROR_CONTEXTS } from "../utils/errorMessages";

const OnboardingContainer = () => {
  const [activeStep, setActiveStep] = useState(1);
  const locale = useContext(LocaleContext);
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isBehalfMode, skipEmailVerification, userRole } = useOnboardingMode();

  const [isLoading, setIsLoading] = useState(false);
  const [savedOk, setSavedOk] = useState("");
  
  // Centralized error handling
  const { handleError, clearError, getMessageBoxProps, errorMessage } = useErrorHandler(ERROR_CONTEXTS.REGISTRATION);

  const [selectedFinancialSystem, setSelectedFinancialSystem] = useState(0);
  const [orgNr, setOrgNr] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [adress, setAdress] = useState("");
  const [postnummer, setPostnummer] = useState("");
  const [poststed, setPoststed] = useState("");

  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [credentials, setCredentials] = useState(null);

  const [accountNumber, setAccountNumber] = useState("");
  const [selectedService, setSlectedService] = useState("");
  const [mva, setMva] = useState("");
  const [otherFinancialSystemName, setOtherFinancialSystemName] = useState("");

  // Clear otherFinancialSystemName when switching away from "Annet"
  useEffect(() => {
    if (selectedFinancialSystem !== null) {
      setOtherFinancialSystemName("");
    }
  }, [selectedFinancialSystem]);

  const goToNextStep = () => setActiveStep((prev) => prev + 1);
  const goToPreviousStep = () => setActiveStep((prev) => prev - 1);
  const goToStep = (step) => setActiveStep(step);

  const createUserAndGoToNextStep = async () => {
    setIsLoading(true);
    clearError();
    setSavedOk("");

    try {
      // Extract document URL from query parameters
      const urlParams = new URLSearchParams(location.search);
      const documentUrl = urlParams.get("documentUrl");

      const userData = {
        email,
        password,
        userName,
        phoneNumber,
        companyName,
        orgNr,
        adress,
        postnummer,
        poststed,
        accountNumber,
        selectedFinancialSystem,
        otherFinancialSystemName,
        selectedService,
        mva,
        documentUrl,
      };

      if (isBehalfMode) {
        // Create client user in behalf mode
        const response = await createClientUser(userData);
        
        setSavedOk(messages[locale]["toastSaveOk"]);
        clearError();

        // Navigate directly to setup with submissionId
        setTimeout(() => {
          navigate(`/onboarding/setup?submissionId=${response.data.submissionId}`);
        }, 1500);
      } else {
        // Normal user creation
        const response = await createUser(userData);

        // Store credentials for email verification step
        setCredentials({ email, password });

        setSavedOk(messages[locale]["toastSaveOk"]);
        clearError();

        // Continue to email verification step
        setTimeout(() => {
          goToNextStep();
        }, 1500);
      }
    } catch (error) {
      // Use centralized error handling
      handleError(error, ERROR_CONTEXTS.REGISTRATION);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IntlProvider locale={locale} messages={messages[locale] || messages.nb}>
      <FlexColumn 
        alignItems="center" 
        maxWidth="960px" 
        margin="0 auto" 
        padding="0 20px"
      >
        {isBehalfMode && (
          <BehalfModeIndicator 
            userRole={userRole}
            clientName={companyName}
          />
        )}
        
        {activeStep <= 3 && (
          <StepIndicator activeStep={activeStep} goToStep={goToStep} />
        )}
        {activeStep === 1 && (
          <OnboardingPage1
            selectedFinancialSystem={selectedFinancialSystem}
            setSelectedFinancialSystem={setSelectedFinancialSystem}
            otherFinancialSystemName={otherFinancialSystemName}
            setOtherFinancialSystemName={setOtherFinancialSystemName}
            orgNr={orgNr}
            setOrgNr={setOrgNr}
            companyName={companyName}
            setCompanyName={setCompanyName}
            adress={adress}
            setAdress={setAdress}
            postnummer={postnummer}
            setPostnummer={setPostnummer}
            poststed={poststed}
            setPoststed={setPoststed}
            goToNextStep={goToNextStep}
          />
        )}
        {activeStep === 2 && (
          <OnboardingPage2
            userName={userName}
            setUserName={setUserName}
            email={email}
            setEmail={setEmail}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            password={password}
            setPassword={setPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            goToNextStep={createUserAndGoToNextStep}
            goToPreviousStep={goToPreviousStep}
            isLoading={isLoading}
            pageHasError={errorMessage}
            savedOk={savedOk}
          />
        )}
        {/* Conditionally show email verification */}
        {activeStep === 3 && !skipEmailVerification && (
          <EmailVerificationContainer 
            credentials={credentials}
            goToNextStep={goToNextStep}
            goToPreviousStep={goToPreviousStep}
          />
        )}
      </FlexColumn>
    </IntlProvider>
  );
};

export default OnboardingContainer;
